import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import AttachmentIcon from '@/assets/icons/attachment-icon.svg?react';
import DismissIcon from '@/assets/icons/dismiss-icon.svg?react';
import FileIcon from '@/assets/icons/file-icon.svg?react';
import SendIcon from '@/assets/icons/send-icon.svg?react';
import { useMessagesMutation } from '@/hooks/useMessages';
import { useScrollbar } from '@/hooks/useScrollbar';
import {
  createFilePreview,
  formatFileSize,
  getAcceptedAttachmentTypesString,
  getMaxFileSize,
} from '../../../utils/file';
import type { AttachedFile } from './types';

/**
 * MessageCompose - Renders message compose component
 */
export const MessageCompose: React.FC = () => {
  const scrollbar = useScrollbar();
  const { t } = useTranslation('common');
  const { handleSendMessage } = useMessagesMutation();
  const [isContentEmpty, setIsContentEmpty] = useState(true);
  const [isSendBtnEnabled, setIsSendBtnEnabled] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isFileSizeExceeded, setIsFileSizeExceeded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const errorToastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const acceptedAttachmentTypes = getAcceptedAttachmentTypesString();

  useEffect(() => {
    return () => {
      if (errorToastTimeoutRef.current) {
        clearTimeout(errorToastTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsSendBtnEnabled(!isContentEmpty || attachedFiles.length > 0);
  }, [isContentEmpty, attachedFiles]);

  // TODO: current adjustment could add new line earlier due to slightly different in height
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      // reset height to calculate new scrollHeight
      textarea.style.height = 'auto';
      // set height based on scroll height, with max height limit, approximately 8 lines
      const maxHeight = 160;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);

      textarea.style.height = `${newHeight}px`;
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;

    if (!files) return;

    const newFiles: AttachedFile[] = [];
    const remainingSlots = 5 - attachedFiles.length;
    const filesToProcess = Array.from(files).slice(0, remainingSlots);

    for (const file of filesToProcess) {
      if (file.size > getMaxFileSize(file)) {
        setIsFileSizeExceeded(true);

        if (errorToastTimeoutRef.current) {
          clearTimeout(errorToastTimeoutRef.current);
        }

        errorToastTimeoutRef.current = setTimeout(() => {
          setIsFileSizeExceeded(false);
        }, 5000);
        // skip this file
        continue;
      }

      const preview = await createFilePreview(file);
      const attachedFile: AttachedFile = {
        id: `${Date.now()}-${Math.random().toString(36)}`, // TODO: may need to change other way to generate id
        name: file.name,
        size: file.size,
        type: file.type,
        preview: preview || undefined,
        data: file,
      };

      newFiles.push(attachedFile);
    }

    setAttachedFiles((prev) => [...prev, ...newFiles]);
    // clear input for same file selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAttachmentBtnClick = () => {
    if (attachedFiles.length < 5) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setAttachedFiles((prev) => prev.filter((file) => file.id !== fileId));
  };

  const handleInputChange = () => {
    setIsContentEmpty(!textareaRef.current?.value?.trim());
    adjustTextareaHeight();
  };

  const handleSendBtnClick = () => {
    const content = textareaRef.current?.value?.trim() || '';
    const files = attachedFiles.map((file) => file.data);

    if (content || attachedFiles.length > 0) {
      handleSendMessage(content, files);
      textareaRef.current!.value = '';
      setAttachedFiles([]);
      setIsSendBtnEnabled(false);
      adjustTextareaHeight();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendBtnClick();
    }
  };

  return (
    <div className="sf:relative sf:p-4">
      <div
        className={classNames({
          'sf:absolute sf:top-2 sf:translate-y-[-100%] sf:left-0 sf:flex sf:items-center sf:justify-between sf:space-x-1 sf:text-red-90 sf:bg-red-10 sf:py-3 sf:px-5 sf:mx-4 sf:rounded-lg sf:text-sm sf:transition-opacity sf:duration-300 sf:ease-in-out':
            true,
          'sf:opacity-0 sf:pointer-events-none': !isFileSizeExceeded,
          'sf:opacity-100 sf:pointer-events-auto': isFileSizeExceeded,
        })}
      >
        <p>{t('chatWindow.fileSizeExceeded')}</p>
        <button
          className="sf:font-medium sf:w-5 sf:h-5 sf:cursor-pointer"
          onClick={() => setIsFileSizeExceeded(false)}
        >
          <DismissIcon className="sf:w-5 sf:h-5" />
        </button>
      </div>

      <div className="sf:flex sf:bg-white sf:gap-2 sf:p-2 sf:border sf:border-gray-200 sf:focus-within:border-widget-button-color sf:rounded-lg sf:focus-within:shadow-lg sf:transition-border sf:duration-150 sf:timing-ease-in-out">
        <div className="sf:flex-1 sf:flex sf:flex-col sf:justify-center sf:min-w-0 sf:gap-1">
          <textarea
            ref={textareaRef}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            rows={1}
            autoFocus={true}
            maxLength={4096}
            spellCheck={false}
            placeholder={t('chatWindow.inputPlaceholder')}
            className="sf:w-full sf:text-base sf:sm:text-sm sf:text-gray-900 sf:placeholder-gray-300 sf:bg-transparent sf:border-none sf:outline-none sf:resize-none sf:min-h-[20px] sf:max-h-[160px] sf:overflow-y-auto sf:caret-widget-button-color"
            style={scrollbar.scrollbarStyle}
            onMouseEnter={scrollbar.onMouseEnter}
            onMouseLeave={scrollbar.onMouseLeave}
          />

          {attachedFiles.length > 0 && (
            <div
              className={classNames({
                'sf:max-w-full sf:pt-1 sf:flex sf:gap-4 sf:overflow-x-auto':
                  true,
                'sf:mb-[-10px]': attachedFiles.length > 1,
                'sf:mb-[1px]': attachedFiles.length === 1,
              })}
              style={scrollbar.scrollbarStyle}
              onMouseEnter={scrollbar.onMouseEnter}
              onMouseLeave={scrollbar.onMouseLeave}
            >
              {attachedFiles.map((file) => (
                <div
                  key={file.id}
                  className="sf:relative sf:overflow-y-visible sf:flex-shrink-0 sf:w-[148px] sf:h-[44px] sf:flex sf:items-center sf:bg-gray-10 sf:rounded-lg sf:p-1"
                >
                  <div
                    className={classNames({
                      'sf:flex sf:items-center sf:justify-center sf:w-9 sf:h-9':
                        true,
                      'sf:mr-1': !file.preview,
                      'sf:mr-3': file.preview,
                    })}
                  >
                    {file.preview ? (
                      <img
                        src={file.preview}
                        alt={file.name}
                        className="sf:w-9 sf:h-9 sf:rounded sf:object-cover"
                      />
                    ) : (
                      <FileIcon className="sf:w-5 sf:h-5 sf:text-gray-600" />
                    )}
                  </div>

                  <div className="sf:flex-1 sf:min-w-0">
                    <div
                      className="sf:font-medium sf:text-gray-900 sf:truncate sf:text-xs sf:leading-4 sf:select-none"
                      title={file.name}
                    >
                      {file.name}
                    </div>
                    <div className="sf:text-gray-80 sf:text-[10px] sf:mt-0.5 sf:select-none">
                      {formatFileSize(file.size)}
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemoveFile(file.id)}
                    className="sf:absolute sf:-top-1 sf:-right-2 sf:w-4 sf:h-4 sf:bg-red-90 sf:rounded-full sf:border-1 sf:border-white sf:hover:bg-red-700 sf:transition-colors sf:cursor-pointer sf:z-10"
                    aria-label="Remove file"
                  >
                    <span className="sf:flex sf:items-center sf:justify-center sf:text-white sf:font-bold sf:text-[8px] sf:mt-[-1px]">
                      —
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sf:flex sf:self-end sf:gap-2">
          <button
            onClick={handleAttachmentBtnClick}
            disabled={attachedFiles.length >= 5}
            className={classNames(
              'sf:w-7 sf:h-7 sf:flex sf:items-center sf:justify-center sf:rounded-lg sf:transition-colors',
              {
                'sf:hover:bg-gray-100 sf:cursor-pointer':
                  attachedFiles.length < 5,
                'sf:opacity-50 sf:cursor-not-allowed':
                  attachedFiles.length >= 5,
              },
            )}
            aria-label={t('chatWindow.attachmentButton')}
          >
            <AttachmentIcon className="sf:w-4 sf:h-4 sf:text-gray-600" />
          </button>

          <button
            onClick={handleSendBtnClick}
            disabled={!isSendBtnEnabled}
            className={classNames({
              'sf:w-7 sf:h-7 sf:flex sf:bg-widget-button-color sf:items-center sf:justify-center sf:rounded-lg sf:transition-all sf:cursor-pointer':
                true,
              'sf:hover:opacity-80': isSendBtnEnabled,
              'sf:disabled:cursor-not-allowed sf:disabled:opacity-30':
                !isSendBtnEnabled,
            })}
            aria-label={t('chatWindow.sendButton')}
          >
            <SendIcon className="sf:w-4 sf:h-4 sf:text-widget-button-text-color" />
          </button>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedAttachmentTypes}
        onChange={handleFileSelect}
        className="sf:hidden"
      />
    </div>
  );
};
